import { invoices, marketKey, offers, takerMarkets } from '@/demo/demoDb';
import {
  BenchmarkPatchBody,
  BenchmarkPostBody,
  ClearingInvoiceStatsRequestBody,
  ClearingInvoiceStatsResponse,
  ClearingInvoicesRequestBody,
  ClearingInvoicesResponse,
  ExpressAcceptPostBody,
  ManageExclusionsBody,
  OfferStatsStatusResponse,
  OffersResponse,
  PatchBody,
  PlayableInvoiceStatsRequestBody,
  PlayableInvoiceStatsResponse,
  PlayableInvoicesRequestBody,
  PlayableInvoicesResponse,
  PostBody,
  RecentClearsResponse,
  StaticPatchBody,
  StaticPostBody,
  SupplierMarketClearsResponse,
  SupplierMarketClearsStatsResponse,
  SupplierMarketsResponse,
} from '@/test/mocks/sharedHandlers';

type OfferRecord = Awaited<ReturnType<typeof offers.findFirst>>;
type TakerMarketRecord = Awaited<ReturnType<typeof takerMarkets.findFirst>>;

/**
 * Computes live eligible/excluded stats for a market slot from the invoices collection.
 *
 * Eligible = not supplierExcluded. Excluded = supplierExcluded. After manageExclusions() mutates
 * the collection, subsequent reads of getSupplierMarkets() and getOffers() will reflect the new
 * counts/amounts automatically — no need to update the takerMarkets collection.
 */
function liveInvoiceStats(key: string): {
  eligibleCount: number;
  eligibleAmount: number;
  eligibleDpeWeightedSum: number;
  eligibleDpeWeightedAverage: number;
  excludedCount: number;
  excludedAmount: number;
} {
  const marketInvoices = invoices
    .findMany((q) => q.where({ marketKey: key }))
    // Invoices awarded in a prior clear (clearedIncluded) are paid and permanently out of the
    // actionable set — never counted as eligible or excluded again, regardless of the offer's
    // cleared flag. This keeps a re-submitted (cleared=false) offer from resurfacing paid invoices
    // while still letting a re-included excluded invoice surface in eligible.
    .filter((inv) => !inv.clearedIncluded);
  const eligible = marketInvoices.filter((inv) => !inv.supplierExcluded);
  const excluded = marketInvoices.filter((inv) => inv.supplierExcluded);
  const eligibleAmount = eligible.reduce((sum, inv) => sum + inv.amount, 0);
  const eligibleDpeWeightedSum = eligible.reduce((sum, inv) => sum + inv.amount * inv.daysPaidEarly, 0);
  return {
    eligibleCount: eligible.length,
    eligibleAmount,
    eligibleDpeWeightedSum,
    eligibleDpeWeightedAverage: eligibleAmount > 0 ? eligibleDpeWeightedSum / eligibleAmount : 0,
    excludedCount: excluded.length,
    excludedAmount: excluded.reduce((sum, inv) => sum + inv.amount, 0),
  };
}

/** Returns an ISO string 2 days after the given date string, or null if the input is null. */
function addTwoDays(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 2);
  return d.toISOString();
}

/**
 * Builds the stats.invoice block for an offer.
 *
 * Before clear: eligible/excluded come from the live invoices collection so exclude/include
 * operations are reflected immediately on the next dashboard refetch. The clearing block is an
 * estimate derived from the live eligible invoices and the entered rate, so the Offer Activity
 * widget's early-payment amount is non-zero and reacts to the rate for an active offer.
 * After clear (offer.cleared = true): all eligible invoices move into awarded + clearing, and
 * eligible drops to zero — matching the post-clear dashboard state a sales rep wants to show.
 */
function computeOfferInvoiceStats(
  offer: NonNullable<OfferRecord>,
  tm: NonNullable<TakerMarketRecord>
): NonNullable<OffersResponse['data'][number]['stats']>['invoice'] {
  const zero = {
    count: 0,
    amount: 0,
    dpeWeightedAverage: 0,
    dpeWeightedSum: 0,
    earn: 0,
    discountWeightedAverage: 0,
    discountSum: 0,
  };
  const live = liveInvoiceStats(tm.key);

  if (offer.cleared) {
    // Use the snapshot taken at clear time — the live collection may have changed since.
    const clearedAmount = offer.clearedEligibleAmount ?? live.eligibleAmount;
    const clearedCount = offer.clearedEligibleCount ?? live.eligibleCount;
    const clearedDpeWeightedSum = offer.clearedEligibleDpeWeightedSum ?? live.eligibleDpeWeightedSum;
    const clearedDpeWeightedAvg = offer.clearedEligibleDpeWeightedAverage ?? live.eligibleDpeWeightedAverage;
    const isDiscountRate = offer.rateType === 'DISC';
    const earn = computeEarn(offer.rateType, offer.rate, clearedAmount, clearedDpeWeightedAvg);
    const awarded = {
      count: clearedCount,
      amount: clearedAmount,
      dpeWeightedAverage: clearedDpeWeightedAvg,
      dpeWeightedSum: clearedDpeWeightedSum,
      earn,
      discountWeightedAverage: isDiscountRate ? offer.rate : 0,
      discountSum: isDiscountRate ? earn : 0,
    };
    const clearedExcludedCount = offer.clearedExcludedCount ?? live.excludedCount;
    const clearedExcludedAmount = offer.clearedExcludedAmount ?? live.excludedAmount;
    return {
      currency: tm.currency,
      eligible: { count: 0, amount: 0, dpeWeightedAverage: 0, dpeWeightedSum: 0 },
      excluded: { count: clearedExcludedCount, amount: clearedExcludedAmount },
      accepted: zero,
      awarded,
      notAccepted: { count: 0, amount: 0, dpeWeightedAverage: 0, dpeWeightedSum: 0, discountSum: 0 },
      clearing: { ...awarded, aprWeightedAverage: 0 },
    };
  }

  // Active (not yet cleared) offer: estimate the clearing figures from the live eligible invoices
  // and the entered rate so the Offer Activity widget shows a non-zero early-payment amount that
  // reacts to the rate. We assume ~100% of eligible is estimated to clear, so clearing mirrors
  // eligible. Same earn formula as the cleared branch. accepted/awarded stay zero — nothing is
  // actually accepted/awarded until a simulated clear.
  // DISC offers state a flat discount percentage applied to the invoice amount, while APR offers
  // accrue earn over the days-paid-early window. The UI derives the early-payment/discount display
  // from clearing.earn and clearing.discount*, so the earn formula must match the offer's rateType.
  const isDiscountRate = offer.rateType === 'DISC';
  const estimatedEarn = computeEarn(offer.rateType, offer.rate, live.eligibleAmount, live.eligibleDpeWeightedAverage);
  const estimatedClearing = {
    count: live.eligibleCount,
    amount: live.eligibleAmount,
    dpeWeightedAverage: live.eligibleDpeWeightedAverage,
    dpeWeightedSum: live.eligibleDpeWeightedSum,
    earn: estimatedEarn,
    discountWeightedAverage: isDiscountRate ? offer.rate : 0,
    discountSum: isDiscountRate ? estimatedEarn : 0,
  };

  return {
    currency: tm.currency,
    eligible: {
      count: live.eligibleCount,
      amount: live.eligibleAmount,
      dpeWeightedAverage: live.eligibleDpeWeightedAverage,
      dpeWeightedSum: live.eligibleDpeWeightedSum,
    },
    excluded: {
      count: live.excludedCount,
      amount: live.excludedAmount,
    },
    accepted: zero,
    awarded: zero,
    notAccepted: { count: 0, amount: 0, dpeWeightedAverage: 0, dpeWeightedSum: 0, discountSum: 0 },
    clearing: { ...estimatedClearing, aprWeightedAverage: 0 },
  };
}

/**
 * Serializes the offers collection into the GET /offers wire response.
 *
 * Each offer record is joined with its takerMarket to build the nested SEA `stats.invoice` block.
 * The `supplierMarkets[].actions` and `editRestrictions` blocks are static defaults — in a real
 * offer they reflect server-side business rules, but for a fresh demo offer all actions are
 * permitted and no restrictions apply.
 */
export function getOffers(): OffersResponse {
  const allOffers = offers.all();
  if (allOffers.length === 0) {
    return { data: [] };
  }

  // Cleared offers are no longer active standing offers — the real API stops returning them
  // in GET /offers once they clear. Post-clear state is shown via lastClearDate and
  // supplier-market-clears, not via an active offer entry.
  const data: OffersResponse['data'] = allOffers
    .filter((offer) => !offer.cleared)
    .map((offer) => {
      const tm = takerMarkets.findFirst((q) => q.where({ key: offer.marketKey }));
      const invoiceStats = tm ? computeOfferInvoiceStats(offer, tm) : undefined;

      const isBenchmark = tm?.pricingType === 'benchmark';
      const rateInformation = isBenchmark
        ? {
            type: 'benchmark' as const,
            rateType: offer.rateType,
            rate: offer.rate,
            rateName: offer.rateName ?? '',
            referenceRate: offer.referenceRate ?? 0,
            spread: offer.spread ?? undefined,
          }
        : {
            type: 'price-discovery' as const,
            rateType: offer.rateType,
            rate: offer.rate,
          };

      return {
        uuid: offer.uuid,
        offerType: isBenchmark ? ('benchmark' as const) : ('price-discovery' as const),
        frequency: offer.frequency,
        status: offer.status,
        excludeNewInvoices: offer.excludeNewInvoices ?? false,
        expirationDate: offer.expirationDate,
        rateInformation,
        supplierMarkets: [
          {
            marketUuid: offer.marketUuid,
            supplierDivisionUuid: offer.supplierDivisionUuid,
            makerOrganizationUuid: offer.makerOrganizationUuid,
            actions: {
              isSubmittedWithZeroAP: false,
              isRateAveraged: false,
              isPreferredRenewalRequested: false,
            },
          },
        ],
        editRestrictions: {
          isDeletable: true,
          isInvoicesEditable: true,
          isRateEditable: true,
          isStatusToggleable: true,
        },
        schedules: [],
        stats: invoiceStats ? { estimatedSuccessRate: 0, hasManualAward: false, invoice: invoiceStats } : undefined,
      };
    });

  return { data };
}

/**
 * Serializes the takerMarkets + offers collections into the GET /supplier-markets wire response.
 *
 * All fields — identity, display, stats, and offer state — are read from the collections seeded at
 * session start. seedDb copies all necessary fields into the takerMarkets collection (including
 * makerLogoPngUrl, resolved from registrationConfig at seed time) so this function stays
 * purely collection-driven.
 */
export function getSupplierMarkets(): SupplierMarketsResponse {
  type Group = SupplierMarketsResponse['data']['supplierMarketGroups'][number];
  type MarketEntry = Group['supplierMarkets'][number];

  const groups: Group[] = takerMarkets.all().map((tm) => {
    // Find only the un-cleared standing offer — a market can have multiple offer records
    // across clear cycles; only the active (non-cleared) one drives the market card state.
    const allMarketOffers = offers.findMany((q) =>
      q.where({ marketKey: marketKey(tm.marketUuid, tm.supplierDivisionUuid) })
    );
    const activeOffer = allMarketOffers.find((o) => !o.cleared) ?? null;

    const entry: MarketEntry = {
      supplier: {
        id: tm.supplierDivisionId,
        uuid: tm.supplierDivisionUuid,
        name: tm.supplierDivisionName,
        buyerId: tm.supplierBuyerId,
        isInactive: false,
        lastInvoiceModificationDate: null,
        instantPay: null,
      },
      market: {
        id: tm.marketId,
        uuid: tm.marketUuid,
        type: tm.marketType,
        enabled: tm.marketEnabled,
        currency: tm.currency,
        marketingDisplayName: tm.marketingDisplayName,
        makerOrganizationUuid: tm.makerOrganizationUuid,
        makerDivisionId: tm.makerDivisionId,
        makerDivisionName: tm.makerDivisionName,
        makerLogoPngUrl: tm.makerLogoPngUrl,
        nextClearTime: tm.nextClearDate,
        goodFundsDate: addTwoDays(tm.payDate),
        payDate: tm.payDate,
        maxApr: tm.maxApr,
        maxDiscount: tm.maxDiscount,
        isPreferredEligible: false,
        isDsf: tm.isDsf,
        isTpf: false,
        isDiscountOnly: false,
        isInstantPayAllowed: false,
        lastClearDate: activeOffer?.clearedAt ?? null,
      },
      // After a simulated clear the offer is no longer an open standing offer — it has
      // cleared. Surface isInOffer: false so formatSEASupplierMarkets treats the slot as
      // post-clear (no active rate card). The awarded/clearing numbers come from the /offers
      // stats block, not from this field.
      offer:
        activeOffer && !activeOffer.cleared
          ? {
              isInOffer: true,
              offerType: tm.pricingType === 'benchmark' ? ('benchmark' as const) : ('price-discovery' as const),
              offerStatus: activeOffer.status,
            }
          : { isInOffer: false, offerType: null, offerStatus: null },
      stats: (() => {
        // liveInvoiceStats excludes invoices awarded in a prior clear, so eligible/excluded reflect
        // only still-actionable invoices. Right after a clear eligible is zero (everything awarded),
        // but a post-clear re-include of a previously-excluded invoice surfaces it here again.
        const live = liveInvoiceStats(marketKey(tm.marketUuid, tm.supplierDivisionUuid));
        return {
          invoice: {
            eligible: {
              count: live.eligibleCount,
              amount: live.eligibleAmount,
              dpeWeightedSum: live.eligibleDpeWeightedSum,
              dpeWeightedAverage: live.eligibleDpeWeightedAverage,
            },
            instantPay: null,
            excluded: {
              count: live.excludedCount,
              amount: live.excludedAmount,
            },
          },
        };
      })(),
    };

    // tm.pricingType is already a validated DemoPricingType — the takerMarkets collection schema
    // (z.enum) guarantees this, so no cast is needed here.
    const pricingType = tm.pricingType;
    // A market with no expressAcceptRate configured has no EA rate at all — resolve to 0 rather
    // than borrowing maxApr/maxDiscount, which represent unrelated rate caps, not an EA rate.
    const resolvedEaRate = tm.expressAcceptRate ?? 0;
    interface OfferOption {
      type: 'price-discovery' | 'static' | 'benchmark' | 'express-accept';
      frequency: 'recurring' | 'one-time';
    }
    const offerOptionsList: OfferOption[] =
      pricingType === 'static'
        ? [{ type: 'static', frequency: 'recurring' }]
        : pricingType === 'benchmark'
        ? [{ type: 'benchmark', frequency: 'recurring' }]
        : [{ type: 'price-discovery', frequency: 'recurring' }];
    if (pricingType === 'price-discovery' && resolvedEaRate > 0) {
      offerOptionsList.push({ type: 'express-accept', frequency: 'one-time' });
    }

    return {
      meta: {
        details: {
          makerOrganizationUuid: tm.makerOrganizationUuid,
          makerDisplayName: tm.makerOrganizationName,
          makerLogoPngUrl: tm.makerLogoPngUrl,
        },
        pricingType,
        isAllEnabledSupplierMarketsInOffer: activeOffer !== null,
        offerDeadline: tm.nextClearDate,
        maxGoodFundsDate: null,
        offerOptions: {
          availableOffer: offerOptionsList,
          newOffer: offerOptionsList,
        },
      },
      supplierMarkets: [entry],
    };
  });

  return { data: { groupingKey: 'SUPPLIER_MARKET', supplierMarketGroups: groups } };
}

/**
 * Returns an empty offer-status response so the polling path resolves pending markets immediately.
 *
 * useOfferStatusPolling (src/lib/offerStatusPolling/useOfferStatusPolling.ts) polls this endpoint
 * every 5 s when enableOfferStatusPolling is ON (always in Split). The API returns only markets
 * that are STILL pending — an empty array means "nothing pending → everything resolved". On the
 * next tick the hook calls removePendingMarkets + refetchManyTakerMarkets, which re-GETs from our
 * state-backed read handlers and surfaces the new offer on the dashboard.
 */
export function getOfferStatsStatus(): OfferStatsStatusResponse {
  return { data: [] };
}

/**
 * Creates a new demo offer in the offers collection from a POST /offers/price-discovery body.
 *
 * Only the price-discovery flow is handled (PATCH = editOffer). If an un-cleared offer already
 * exists for the market, it is updated in-place (idempotent re-submit). If the previous offer
 * was cleared, a new offer record is created with a fresh UUID so the cleared record stays in
 * the collection and appears in history.
 *
 * Returns the uuid to include in the 201 response body (useSubmitPriceDiscoveryOffer reads
 * `body.data[0]?.uuid` to wire up the optimistic update).
 */
export async function submitOffer(body: PostBody): Promise<string> {
  const sm = body.supplierMarkets?.[0];
  if (!sm) {
    throw new Error('submitOffer: missing supplierMarkets[0]');
  }

  const key = marketKey(sm.marketUuid, sm.supplierDivisionUuid);

  // Find all offers for this market. Cleared offers must NOT be reset — they represent
  // historical clears that getSupplierMarketClears/getRecentClears must return. Only an
  // un-cleared (active/paused) offer is eligible for an in-place update.
  const existingOffers = offers.findMany((q) => q.where({ marketKey: key }));
  const existingActive = existingOffers.find((o) => !o.cleared);

  if (existingActive) {
    // An un-cleared offer already exists — update it in-place (idempotent re-submit).
    await offers.update((q) => q.where({ uuid: existingActive.uuid }), {
      data(record) {
        record.rateType = (body.rateType as 'APR' | 'DISC') ?? 'APR';
        record.rate = body.rate ?? 0;
        record.frequency = normalizeFrequency(body.frequency);
        record.status = 'active';
        record.expirationDate = body.expirationDate ?? null;
      },
    });
    return existingActive.uuid;
  }

  // No active offer — create a fresh one. UUID is random because it only needs to be unique
  // within the session; the app holds it in memory (POST 201 → PATCH/toggle/clear) and
  // lookups always go through marketKey + !cleared, never through the UUID.
  const offerUuid = `DEMO-OFFER-${crypto.randomUUID()}`;

  const tm = takerMarkets.findFirst((q) => q.where({ key }));
  await offers.create({
    uuid: offerUuid,
    marketKey: key,
    marketUuid: sm.marketUuid,
    supplierDivisionUuid: sm.supplierDivisionUuid,
    makerOrganizationUuid: sm.makerOrganizationUuid ?? tm?.makerOrganizationUuid ?? '',
    rateType: (body.rateType as 'APR' | 'DISC') ?? 'APR',
    rate: body.rate ?? 0,
    frequency: normalizeFrequency(body.frequency),
    status: 'active',
    excludeNewInvoices: null,
    expirationDate: body.expirationDate ?? null,
    rateName: null,
    referenceRate: null,
    spread: null,
    cleared: false,
    clearedAt: null,
    clearedEligibleCount: null,
    clearedEligibleAmount: null,
    clearedEligibleDpeWeightedSum: null,
    clearedEligibleDpeWeightedAverage: null,
    clearedExcludedCount: null,
    clearedExcludedAmount: null,
  });

  return offerUuid;
}

/**
 * Updates an existing demo offer in the offers collection from a PATCH /offers/price-discovery body.
 *
 * The PATCH path is the edit flow — the offer uuid is already known (set when it was first
 * submitted). We update only the fields the user changed; uuid and market identity are immutable.
 * Returns 202 so useSubmitPriceDiscoveryOffer routes through addPendingMarkets → poll path.
 */
export async function editOffer(body: PatchBody): Promise<void> {
  const offerUuid = body.offers?.[0]?.uuid;
  if (!offerUuid) {
    throw new Error('editOffer: missing offers[0].uuid');
  }

  await offers.update((q) => q.where({ uuid: offerUuid }), {
    data(record) {
      if (body.rateInformation?.rateType) {
        record.rateType = body.rateInformation.rateType as 'APR' | 'DISC';
      }

      if (body.rateInformation?.rate !== undefined) {
        record.rate = body.rateInformation.rate;
      }

      if (body.frequency) {
        record.frequency = normalizeFrequency(body.frequency);
      }

      if (body.status) {
        record.status = body.status as 'active' | 'paused' | 'inactive';
      }

      if (body.expirationDate !== undefined) {
        record.expirationDate = body.expirationDate ?? null;
      }
    },
  });
}

/**
 * Toggles a demo offer's participation on/off from PATCH /offers/{uuid}/toggle-status.
 *
 * "Turn participation off" flips an active offer to paused (and back), mirroring the SEA
 * toggle-status semantics. The status drives offerConfig.isEnabled in formatSEASupplierMarkets,
 * so on the next refetch the market card reflects the paused/active state.
 */
export async function toggleOffer(uuid: string): Promise<void> {
  const existing = offers.findFirst((q) => q.where({ uuid }));
  if (!existing) {
    return;
  }

  await offers.update((q) => q.where({ uuid }), {
    data(record) {
      record.status = record.status === 'active' ? 'paused' : 'active';
    },
  });
}

/**
 * Simulates a market clear for a given offer UUID.
 *
 * Sets cleared=true and stamps clearedAt to the current ISO time. On the next read of getOffers()
 * and getSupplierMarkets(), computeOfferInvoiceStats sees cleared=true and moves all eligible
 * invoices into awarded+clearing stats, while the supplier-markets eligible block drops to zero.
 * The caller is responsible for triggering a taker-markets refetch after this mutation.
 */
export async function clearMarket(offerUuid: string): Promise<void> {
  const existing = offers.findFirst((q) => q.where({ uuid: offerUuid }));
  if (!existing) {
    return;
  }

  // Snapshot the live eligible stats now so post-clear reads use the correct
  // included-invoice values even if exclusions change afterwards.
  const snap = liveInvoiceStats(existing.marketKey);

  // Stamp clearedIncluded=true on every invoice that was included at clear time so that
  // post-clear include/exclude actions cannot accidentally resurface them in the included tab.
  const includedAtClear = invoices.findMany((q) => q.where({ marketKey: existing.marketKey, supplierExcluded: false }));
  for (const inv of includedAtClear) {
    await invoices.update((q) => q.where({ voucherId: inv.voucherId }), {
      data(record) {
        record.clearedIncluded = true;
      },
    });
  }

  await offers.update((q) => q.where({ uuid: offerUuid }), {
    data(record) {
      record.cleared = true;
      record.clearedAt = new Date().toISOString();
      record.clearedEligibleCount = snap.eligibleCount;
      record.clearedEligibleAmount = snap.eligibleAmount;
      record.clearedEligibleDpeWeightedSum = snap.eligibleDpeWeightedSum;
      record.clearedEligibleDpeWeightedAverage = snap.eligibleDpeWeightedAverage;
      record.clearedExcludedCount = snap.excludedCount;
      record.clearedExcludedAmount = snap.excludedAmount;
    },
  });
}

/**
 * Returns the uuids of all offers that are active and have eligible invoices — the set the
 * SimulateClearDrawer lists as selectable for a demo clear.
 */
export function getActiveOffersWithEligibleInvoices(): Array<{
  offerUuid: string;
  marketUuid: string;
  supplierDivisionUuid: string;
  makerOrganizationName: string;
  marketDisplayName: string;
  includedInvoiceCount: number;
  includedInvoiceAmount: number;
  totalInvoiceCount: number;
  totalInvoiceAmount: number;
  currency: string;
  cleared: boolean;
}> {
  return offers
    .all()
    .filter((offer) => offer.status === 'active' && !offer.cleared)
    .flatMap((offer) => {
      const tm = takerMarkets.findFirst((q) => q.where({ key: offer.marketKey }));
      if (!tm) return [];
      // liveInvoiceStats already drops invoices awarded in a prior clear, so the drawer counts only
      // the invoices still available to clear (e.g. ones re-included from the excluded tab).
      const live = liveInvoiceStats(offer.marketKey);
      const totalCount = live.eligibleCount + live.excludedCount;
      if (totalCount === 0) return [];
      return [
        {
          offerUuid: offer.uuid,
          marketUuid: offer.marketUuid,
          supplierDivisionUuid: offer.supplierDivisionUuid,
          makerOrganizationName: tm.makerOrganizationName,
          marketDisplayName: tm.marketingDisplayName,
          includedInvoiceCount: live.eligibleCount,
          includedInvoiceAmount: live.eligibleAmount,
          totalInvoiceCount: totalCount,
          totalInvoiceAmount: live.eligibleAmount + live.excludedAmount,
          currency: tm.currency,
          cleared: offer.cleared,
        },
      ];
    });
}

/**
 * Reads the invoices collection and returns the POST /playable-invoices wire response.
 *
 * Applies the same filters the real API supports that the invoice table relies on:
 *   - supplierMarkets: scope to the requested (marketUuid, supplierDivisionUuid) pairs
 *   - search.supplierExcluded: tab filter used ONLY by OfferBuilder's EditInvoices tabs —
 *     true = excluded tab, false = included tab. When omitted (Invoice Central), ALL playable
 *     invoices are returned (both included and excluded); the client derives the
 *     Eligible/Excluded/In-an-Offer chip from supplierExcluded + market offer state
 *     (see InvoiceCentral/Eligible/utils.ts).
 *   - search.voucherPatterns: simple substring match used by the search bar
 *
 * After manageExclusions() toggles supplierExcluded flags, subsequent refetches see the
 * updated rows immediately.
 */
export function getPlayableInvoices(body: PlayableInvoicesRequestBody): PlayableInvoicesResponse {
  const requestedKeys = body.supplierMarkets.map((sm) => marketKey(sm.marketUuid, sm.supplierDivisionUuid));

  const marketKeys = new Set(requestedKeys);

  let filtered = invoices.all().filter((inv) => {
    if (!marketKeys.has(inv.marketKey)) return false;
    // Invoices awarded in a prior clear (clearedIncluded) are paid and permanently hidden from both
    // tabs, regardless of the offer's cleared flag (a re-submitted offer resets cleared to false).
    // Invoices excluded at clear time stay visible and their supplierExcluded flag toggles freely.
    return !inv.clearedIncluded;
  });

  // Tab filter: only applied when the caller explicitly requests one side (OfferBuilder's
  // included/excluded tabs). Invoice Central omits supplierExcluded and expects ALL playable
  // invoices — the client renders the Excluded chip from the row's supplierExcluded flag, so
  // dropping excluded rows here would make them silently disappear instead of showing "Excluded".
  if (body.search?.supplierExcluded !== undefined) {
    const wantExcluded = body.search.supplierExcluded;
    filtered = filtered.filter((inv) => inv.supplierExcluded === wantExcluded);
  }

  // Voucher search (simple substring, case-insensitive)
  if (body.search?.voucherPatterns?.length) {
    const patterns = body.search.voucherPatterns.map((p) => p.toLowerCase());
    filtered = filtered.filter((inv) => patterns.some((p) => inv.voucherId.toLowerCase().includes(p)));
  }

  return {
    data: {
      invoices: filtered.map((inv) => ({
        supplierDivisionUuid: inv.supplierDivisionUuid,
        marketUuid: inv.marketUuid,
        invoiceId: null,
        voucherId: inv.voucherId,
        currency: inv.currency,
        originalAmount: inv.originalAmount,
        adjustmentAmount: null,
        amount: inv.amount,
        originalDepositDate: inv.originalDepositDate || null,
        daysPaidEarly: inv.daysPaidEarly,
        supplierExcluded: inv.supplierExcluded,
        minDiscount: null,
        coversUnmatched: false,
        isAccepted: false,
        earn: null,
      })),
      pageInfo: { hasNextPage: false },
    },
  };
}

const PLAYABLE_CSV_HEADERS = [
  'voucherId',
  'amount',
  'originalAmount',
  'originalDepositDate',
  'daysPaidEarly',
  'currency',
  'supplierExcluded',
] as const;

function csvCell(value: string | number | boolean | null): string {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Serializes the same live, filtered rows used by the playable-invoice table. */
export function getPlayableInvoicesCsv(body: PlayableInvoicesRequestBody): string {
  const rows = getPlayableInvoices(body).data.invoices.map((invoice) =>
    [
      invoice.voucherId,
      invoice.amount,
      invoice.originalAmount,
      invoice.originalDepositDate,
      invoice.daysPaidEarly,
      invoice.currency,
      invoice.supplierExcluded ?? false,
    ]
      .map(csvCell)
      .join(',')
  );

  return [PLAYABLE_CSV_HEADERS.join(','), ...rows].join('\n');
}

function dateOnly(value: string | null | undefined): string {
  return value?.slice(0, 10) ?? '';
}

function buildClearingInvoices(
  body: ClearingInvoicesRequestBody | ClearingInvoiceStatsRequestBody
): ClearingInvoicesResponse['data']['invoices'] {
  const requestedKeys = new Set(
    body.supplierMarkets.map((market) => marketKey(market.marketUuid, market.supplierDivisionUuid))
  );
  const voucherPatterns = body.search?.voucherPatterns?.map((pattern) => pattern.toLowerCase());

  return invoices
    .all()
    .filter((invoice) => invoice.clearedIncluded && requestedKeys.has(invoice.marketKey))
    .filter(
      (invoice) =>
        !voucherPatterns?.length ||
        voucherPatterns.some((pattern) => invoice.voucherId.toLowerCase().includes(pattern.replace(/\*/g, '')))
    )
    .flatMap((invoice) => {
      const offer = offers
        .findMany((query) => query.where({ marketKey: invoice.marketKey }))
        .find((item) => item.cleared);
      const takerMarket = takerMarkets.findFirst((query) => query.where({ key: invoice.marketKey }));
      if (!offer || !takerMarket) {
        return [];
      }

      const clearingStatus = 'clearing' as const;
      if (body.search?.clearingStatus?.length && !body.search.clearingStatus.includes(clearingStatus)) {
        return [];
      }

      if (body.search?.clearingCategory?.length && !body.search.clearingCategory.includes('awarded')) {
        return [];
      }

      const earn = computeEarn(offer.rateType, offer.rate, invoice.originalAmount, invoice.daysPaidEarly);
      const minDiscount =
        offer.rateType === 'DISC' ? offer.rate : invoice.originalAmount > 0 ? (earn / invoice.originalAmount) * 100 : 0;
      const clearDate = dateOnly(offer.clearedAt);
      const scheduledClearDate = dateOnly(takerMarket.nextClearDate ?? offer.clearedAt);

      return [
        {
          supplierDivisionUuid: invoice.supplierDivisionUuid,
          marketUuid: invoice.marketUuid,
          voucherId: invoice.voucherId,
          dueDate: dateOnly(invoice.originalDepositDate),
          newPayDate: dateOnly(takerMarket.payDate ?? takerMarket.nextClearDate ?? offer.clearedAt),
          ...(scheduledClearDate && { scheduledClearDate }),
          amount: invoice.amount,
          currency: invoice.currency,
          minDiscount,
          earn,
          isAwarded: true,
          isCleared: false,
          isPendingClear: false,
          isInCashPool: false,
          clearingStatus,
          clearingCategory: 'awarded' as const,
          // Keep the source clear available through the existing fallback date when no schedule exists.
          ...(!scheduledClearDate && clearDate ? { scheduledClearDate: clearDate } : {}),
        },
      ];
    });
}

/** Returns live awarded rows after Simulate Clear stamps `clearedIncluded`. */
export function getClearingInvoices(body: ClearingInvoicesRequestBody): ClearingInvoicesResponse {
  return {
    data: {
      invoices: buildClearingInvoices(body),
      pageInfo: { hasNextPage: false },
    },
  };
}

/** Computes the awarded amount/count summary from the same rows as the clearing list. */
export function getClearingInvoiceStats(body: ClearingInvoiceStatsRequestBody): ClearingInvoiceStatsResponse {
  const clearingInvoices = buildClearingInvoices(body);
  const supplierMarkets = body.supplierMarkets.map((market) => {
    const marketInvoices = clearingInvoices.filter(
      (invoice) =>
        invoice.marketUuid === market.marketUuid && invoice.supplierDivisionUuid === market.supplierDivisionUuid
    );
    const takerMarket = takerMarkets.findFirst((query) =>
      query.where({ key: marketKey(market.marketUuid, market.supplierDivisionUuid) })
    );

    return {
      ...market,
      currency: takerMarket?.currency ?? marketInvoices[0]?.currency ?? '',
      count: marketInvoices.length,
      amount: marketInvoices.reduce((sum, invoice) => sum + invoice.amount, 0),
    };
  });

  return {
    data: {
      makerOrganizationUuid: body.makerOrganizationUuid,
      summary: {
        count: clearingInvoices.length,
        amount: clearingInvoices.reduce((sum, invoice) => sum + invoice.amount, 0),
      },
      supplierMarkets,
    },
  };
}

/**
 * Reads the invoices collection and computes per-market playable/offerable stats.
 *
 * Offerable = invoices where supplierExcluded is false. After include/exclude operations,
 * these stats update automatically on the next refetch so the invoice sidebar reflects the
 * new totals without a dashboard-level refetch.
 */
export function getPlayableInvoiceStats(body?: PlayableInvoiceStatsRequestBody): PlayableInvoiceStatsResponse {
  const allInvoices = invoices.all();
  const allTakerMarkets = takerMarkets.all();
  const makerOrganizationUuid = allTakerMarkets[0]?.makerOrganizationUuid ?? '';

  // When the request specifies supplierMarkets, scope stats to only those markets.
  const requestedKeys =
    body?.supplierMarkets && body.supplierMarkets.length > 0
      ? new Set(body.supplierMarkets.map((sm) => marketKey(sm.marketUuid, sm.supplierDivisionUuid)))
      : null;

  const scopedTakerMarkets = requestedKeys
    ? allTakerMarkets.filter((tm) => requestedKeys.has(tm.key))
    : allTakerMarkets;

  const supplierMarketEntries = scopedTakerMarkets.map((tm) => {
    // Invoices awarded in a prior clear (clearedIncluded) are paid and permanently out of the
    // playable set, regardless of the offer's cleared flag. Excluded-at-clear invoices stay.
    const marketInvoices = allInvoices.filter((inv) => inv.marketKey === tm.key && !inv.clearedIncluded);
    const offerableInvoices = marketInvoices.filter((inv) => !inv.supplierExcluded);

    const playable = {
      count: marketInvoices.length,
      amount: marketInvoices.reduce((sum, inv) => sum + inv.amount, 0),
      dpeWeightedSum: marketInvoices.reduce((sum, inv) => sum + inv.amount * inv.daysPaidEarly, 0),
      dpeWeightedAverage:
        marketInvoices.length > 0
          ? marketInvoices.reduce((sum, inv) => sum + inv.amount * inv.daysPaidEarly, 0) /
            marketInvoices.reduce((sum, inv) => sum + inv.amount, 0)
          : 0,
      earliestOriginalDepositDate: null,
    };

    const offerableAmount = offerableInvoices.reduce((sum, inv) => sum + inv.amount, 0);
    const offerable = {
      count: offerableInvoices.length,
      amount: offerableAmount,
      dpeWeightedSum: offerableInvoices.reduce((sum, inv) => sum + inv.amount * inv.daysPaidEarly, 0),
      dpeWeightedAverage:
        offerableInvoices.length > 0
          ? offerableInvoices.reduce((sum, inv) => sum + inv.amount * inv.daysPaidEarly, 0) / offerableAmount
          : 0,
      earliestOriginalDepositDate: null,
    };

    return {
      supplierDivisionUuid: tm.supplierDivisionUuid,
      marketUuid: tm.marketUuid,
      currency: tm.currency,
      earliestEarlyPayDepositDate: null,
      playable,
      offerable,
    };
  });

  return { data: { makerOrganizationUuid, supplierMarkets: supplierMarketEntries } };
}

/**
 * Applies include/exclude operations to the invoices collection.
 *
 * Supports the four call shapes used by useInvoiceEdits:
 *   - operation=INCLUDE (no overrides): clear supplierExcluded on all invoices for the market
 *   - operation=EXCLUDE (no overrides): set supplierExcluded on all invoices for the market
 *   - overrides.exclude.vouchers: set supplierExcluded=true on specific vouchers
 *   - overrides.include.vouchers: set supplierExcluded=false on specific vouchers
 *
 * Complex filter shapes (search, mode=FOCUSING, categoryGroupingOperator) are not modeled —
 * they fall through to the operation fallback or are no-ops. The demo use case is
 * "exclude selected" / "include selected" / "include all" / "exclude all".
 *
 * Returns the updated manage-exclusions stats response (same shape as PlayableInvoiceStats).
 */
export async function manageExclusions(body: ManageExclusionsBody): Promise<PlayableInvoiceStatsResponse['data']> {
  const { supplierMarkets: requestedMarkets, operation, overrides } = body;

  for (const sm of requestedMarkets) {
    const key = marketKey(sm.marketUuid, sm.supplierDivisionUuid);

    if (overrides?.exclude?.vouchers.length) {
      const voucherSet = new Set(overrides.exclude.vouchers);
      const targets = invoices
        .findMany((q) => q.where({ marketKey: key }))
        .filter((inv) => voucherSet.has(inv.voucherId));
      for (const inv of targets) {
        await invoices.update((q) => q.where({ voucherId: inv.voucherId, marketKey: key }), {
          data(record) {
            record.supplierExcluded = true;
          },
        });
      }
    } else if (overrides?.include?.vouchers.length) {
      const voucherSet = new Set(overrides.include.vouchers);
      const targets = invoices
        .findMany((q) => q.where({ marketKey: key }))
        .filter((inv) => voucherSet.has(inv.voucherId));
      for (const inv of targets) {
        await invoices.update((q) => q.where({ voucherId: inv.voucherId, marketKey: key }), {
          data(record) {
            record.supplierExcluded = false;
          },
        });
      }
    } else if (operation === 'EXCLUDE') {
      const targets = invoices.findMany((q) => q.where({ marketKey: key }));
      for (const inv of targets) {
        await invoices.update((q) => q.where({ voucherId: inv.voucherId, marketKey: key }), {
          data(record) {
            record.supplierExcluded = true;
          },
        });
      }
    } else if (operation === 'INCLUDE') {
      const targets = invoices.findMany((q) => q.where({ marketKey: key }));
      for (const inv of targets) {
        await invoices.update((q) => q.where({ voucherId: inv.voucherId, marketKey: key }), {
          data(record) {
            record.supplierExcluded = false;
          },
        });
      }
    }
  }

  return getPlayableInvoiceStats().data;
}

/**
 * Computes the earn amount for a cleared offer given the snapshot amounts and DPE.
 *
 * Shared by computeOfferInvoiceStats and the history serializers so the earn formula is
 * consistent across the offers stats block and the supplier-market-clears wire response.
 */
export function computeEarn(
  rateType: 'APR' | 'DISC',
  rate: number,
  amount: number,
  dpeWeightedAverage: number
): number {
  const rateFraction = rate / 100;
  return rateType === 'DISC' ? amount * rateFraction : amount * (rateFraction / 365) * dpeWeightedAverage;
}

/**
 * Builds a single clear entry shared between getSupplierMarketClears and getRecentClears.
 *
 * The depositDate is derived from the clearedAt timestamp (YYYY-MM-DD). The payDate comes
 * from the takerMarket's payDate or nextClearDate — the market's scheduled pay date is the
 * best approximation available in demo context.
 */
function buildClearEntry(
  offer: NonNullable<OfferRecord>,
  tm: NonNullable<TakerMarketRecord>
): {
  payDate: string;
  depositDate: string;
  clearedInvoiceAmount: number;
  clearedDiscountedAmount: number;
  clearedEarn: number;
  clearedDiscountWeightedAvg: number;
  clearedAprWeightedAvg: number;
  clearedDpeWeightedAvg: number;
  clearedInvoiceCount: number;
  isPendingClear: false;
  isPendingMarketClear: false;
  isInstantPay: false;
  currency: string;
  supplierMarket: {
    makerOrganizationUuid: string;
    marketUuid: string;
    supplierDivisionUuid: string;
    makerOrganizationName: string;
  };
} {
  const amount = offer.clearedEligibleAmount ?? 0;
  const dpe = offer.clearedEligibleDpeWeightedAverage ?? 0;
  const earn = computeEarn(offer.rateType, offer.rate, amount, dpe);
  const isDisc = offer.rateType === 'DISC';
  // Effective discount % of the clear: earn as a fraction of invoice amount. For DISC this equals
  // offer.rate (earn = amount * rate/100); for APR it's the annualized rate prorated over DPE —
  // matching the real SEA calculation for clearedDiscountWeightedAvg.
  const effectiveDiscountPct = amount > 0 ? (earn / amount) * 100 : 0;

  // clearedAt is a full ISO string; slice to YYYY-MM-DD for the depositDate field
  const depositDate = (offer.clearedAt ?? new Date().toISOString()).slice(0, 10);
  const payDate = tm.payDate ?? tm.nextClearDate ?? depositDate;

  return {
    payDate,
    depositDate,
    clearedInvoiceAmount: amount,
    clearedDiscountedAmount: amount - earn,
    clearedEarn: earn,
    clearedDiscountWeightedAvg: effectiveDiscountPct,
    clearedAprWeightedAvg: isDisc ? 0 : offer.rate,
    clearedDpeWeightedAvg: dpe,
    clearedInvoiceCount: offer.clearedEligibleCount ?? 0,
    isPendingClear: false,
    isPendingMarketClear: false,
    isInstantPay: false,
    currency: tm.currency,
    supplierMarket: {
      makerOrganizationUuid: tm.makerOrganizationUuid,
      marketUuid: tm.marketUuid,
      supplierDivisionUuid: tm.supplierDivisionUuid,
      makerOrganizationName: tm.makerOrganizationName,
    },
  };
}

/**
 * Serializes cleared offers into the GET /supplier-market-clears wire response.
 *
 * Reads `offers.all().filter(o => o.cleared)` and builds a clear entry per offer.
 * Supports `startDepositDate`/`endDepositDate`/`isPendingClear` query params so the
 * history page's date-range filter and the widget's 30-day window work correctly.
 * Pagination is computed from the filtered set; all results are returned on one page
 * since demo sessions accumulate at most a handful of clears.
 */
export function getSupplierMarketClears(request: Request): SupplierMarketClearsResponse {
  const params = new URL(request.url).searchParams;
  const startDepositDate = params.get('startDepositDate');
  const endDepositDate = params.get('endDepositDate');
  const isPendingClearParam = params.get('isPendingClear');
  const makerOrgUuids = params.getAll('makerOrganizationUuids');

  let clears = offers
    .all()
    .filter((o) => o.cleared)
    .flatMap((offer) => {
      const tm = takerMarkets.findFirst((q) => q.where({ key: offer.marketKey }));
      if (!tm) return [];
      return [{ uuid: `DEMO-CLEAR-${offer.uuid}`, clearIds: [1], statementIds: [], ...buildClearEntry(offer, tm) }];
    });

  // Filter by maker org UUIDs when the customer filter is active (matches real API behaviour)
  if (makerOrgUuids.length > 0) {
    const uuidSet = new Set(makerOrgUuids);
    clears = clears.filter((c) => uuidSet.has(c.supplierMarket.makerOrganizationUuid));
  }

  // Scope to the specific supplier market the clear history page requests.
  // The real API filters server-side; the client does not re-filter after receiving the response.
  const smMarketUuid = params.get('supplierMarkets[0][marketUuid]');
  const smSupplierDivisionUuid = params.get('supplierMarkets[0][supplierDivisionUuid]');
  const smMakerOrganizationUuid = params.get('supplierMarkets[0][makerOrganizationUuid]');

  if (smMarketUuid || smSupplierDivisionUuid || smMakerOrganizationUuid) {
    clears = clears.filter(
      (c) =>
        (!smMarketUuid || c.supplierMarket.marketUuid === smMarketUuid) &&
        (!smSupplierDivisionUuid || c.supplierMarket.supplierDivisionUuid === smSupplierDivisionUuid) &&
        (!smMakerOrganizationUuid || c.supplierMarket.makerOrganizationUuid === smMakerOrganizationUuid)
    );
  }

  // Apply isPendingClear filter — demo has no pending clears, but honour the param for correctness
  if (isPendingClearParam !== null) {
    const wantPending = isPendingClearParam === 'true';
    clears = clears.filter((c) => c.isPendingClear === wantPending);
  }

  if (startDepositDate) {
    clears = clears.filter((c) => c.depositDate >= startDepositDate);
  }

  // An empty endDepositDate means "no upper bound" (include future dates)
  if (endDepositDate) {
    clears = clears.filter((c) => c.depositDate <= endDepositDate);
  }

  const totalCount = clears.length;

  return {
    data: {
      groupingKey: 'SUPPLIER_MARKET',
      clears,
      pagination: {
        page: 1,
        limit: 25,
        totalCount,
        totalPages: totalCount > 0 ? 1 : 0,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    },
  };
}

/**
 * Serializes cleared offers into the GET /supplier-market-clears/recent wire response.
 *
 * Used as an existence probe by both EarlyPayHistoryWidget and ConsolidatedHistory —
 * a non-empty array means "there is some history". No date filtering needed.
 */
export function getRecentClears(): RecentClearsResponse {
  const clears = offers
    .all()
    .filter((o) => o.cleared)
    .flatMap((offer, i) => {
      const tm = takerMarkets.findFirst((q) => q.where({ key: offer.marketKey }));
      if (!tm) return [];
      return [{ id: i + 1, clearTime: offer.clearedAt ?? new Date().toISOString(), ...buildClearEntry(offer, tm) }];
    });

  return { data: { clears } };
}

/**
 * Aggregates cleared offers into the GET /supplier-market-clears/stats wire response.
 *
 * Computes totals and weighted averages across all cleared offers. The discount weighted
 * average is weighted by clearedInvoiceAmount so it reflects the portfolio-level discount
 * rate, matching the real API's calculation.
 * Returns an all-zero response when no clears have occurred yet.
 */
export function getSupplierMarketClearsStats(request: Request): SupplierMarketClearsStatsResponse {
  const params = new URL(request.url).searchParams;
  const smMarketUuid = params.get('supplierMarkets[0][marketUuid]');
  const smSupplierDivisionUuid = params.get('supplierMarkets[0][supplierDivisionUuid]');
  const smMakerOrganizationUuid = params.get('supplierMarkets[0][makerOrganizationUuid]');

  let clearedOffers = offers.all().filter((o) => o.cleared);

  if (smMarketUuid || smSupplierDivisionUuid || smMakerOrganizationUuid) {
    clearedOffers = clearedOffers.filter((o) => {
      const tm = takerMarkets.findFirst((q) => q.where({ key: o.marketKey }));
      if (!tm) return false;
      return (
        (!smMarketUuid || tm.marketUuid === smMarketUuid) &&
        (!smSupplierDivisionUuid || tm.supplierDivisionUuid === smSupplierDivisionUuid) &&
        (!smMakerOrganizationUuid || tm.makerOrganizationUuid === smMakerOrganizationUuid)
      );
    });
  }

  if (clearedOffers.length === 0) {
    const currency = takerMarkets.all()[0]?.currency ?? 'USD';
    return {
      data: {
        clearedInvoiceAmount: 0,
        clearedEarn: 0,
        clearedDiscountWeightedAvg: 0,
        currency,
        minimumClearPayDate: '',
        minimumClearDepositDate: '',
        latestClearPayDate: '',
        latestClearDepositDate: '',
      },
    };
  }

  const entries = clearedOffers.flatMap((offer) => {
    const tm = takerMarkets.findFirst((q) => q.where({ key: offer.marketKey }));
    if (!tm) return [];
    return [{ offer, tm, clear: buildClearEntry(offer, tm) }];
  });

  const totalInvoiceAmount = entries.reduce((sum, e) => sum + e.clear.clearedInvoiceAmount, 0);
  const totalEarn = entries.reduce((sum, e) => sum + e.clear.clearedEarn, 0);

  // Weighted avg discount: sum(discountRate * invoiceAmount) / totalInvoiceAmount
  const discountWeightedSum = entries.reduce(
    (sum, e) => sum + e.clear.clearedDiscountWeightedAvg * e.clear.clearedInvoiceAmount,
    0
  );
  const clearedDiscountWeightedAvg = totalInvoiceAmount > 0 ? discountWeightedSum / totalInvoiceAmount : 0;

  const depositDates = entries.map((e) => e.clear.depositDate).filter(Boolean);
  const payDates = entries.map((e) => e.clear.payDate).filter(Boolean);
  const currency = entries[0]?.tm.currency ?? 'USD';

  return {
    data: {
      clearedInvoiceAmount: totalInvoiceAmount,
      clearedEarn: totalEarn,
      clearedDiscountWeightedAvg,
      currency,
      minimumClearDepositDate: depositDates.reduce((a, b) => (a < b ? a : b), depositDates[0] ?? ''),
      minimumClearPayDate: payDates.reduce((a, b) => (a < b ? a : b), payDates[0] ?? ''),
      latestClearDepositDate: depositDates.reduce((a, b) => (a > b ? a : b), depositDates[0] ?? ''),
      latestClearPayDate: payDates.reduce((a, b) => (a > b ? a : b), payDates[0] ?? ''),
    },
  };
}

/**
 * Creates or updates a benchmark offer from POST /offers/benchmark.
 *
 * Benchmark offers do not carry a user-entered rate — the rate is platform-determined from the
 * market's referenceRate + spread. The offer record is seeded with those values from the
 * takerMarkets collection so getOffers() can return the correct rateInformation.
 *
 * Returns the uuid for the 201 response body (useSubmitBenchmarkOffer reads body.data[0]?.uuid).
 */
export async function submitBenchmarkOffer(body: BenchmarkPostBody): Promise<string> {
  const sm = body.supplierMarkets?.[0];
  if (!sm) {
    throw new Error('submitBenchmarkOffer: missing supplierMarkets[0]');
  }

  const key = marketKey(sm.marketUuid, sm.supplierDivisionUuid);
  const tm = takerMarkets.findFirst((q) => q.where({ key }));

  const existingOffers = offers.findMany((q) => q.where({ marketKey: key }));
  const existingActive = existingOffers.find((o) => !o.cleared);

  if (existingActive) {
    await offers.update((q) => q.where({ uuid: existingActive.uuid }), {
      data(record) {
        record.frequency = normalizeFrequency(body.frequency);
        record.status = 'active';
        record.expirationDate = body.expirationDate ?? null;
      },
    });
    return existingActive.uuid;
  }

  const offerUuid = `DEMO-OFFER-${crypto.randomUUID()}`;
  const rate = (tm?.referenceRate ?? 0) + (tm?.spread ?? 0);

  await offers.create({
    uuid: offerUuid,
    marketKey: key,
    marketUuid: sm.marketUuid,
    supplierDivisionUuid: sm.supplierDivisionUuid,
    makerOrganizationUuid: sm.makerOrganizationUuid ?? tm?.makerOrganizationUuid ?? '',
    rateType: tm?.rateType ?? 'APR',
    rate,
    frequency: normalizeFrequency(body.frequency),
    status: 'active',
    excludeNewInvoices: null,
    expirationDate: body.expirationDate ?? null,
    rateName: tm?.rateName ?? null,
    referenceRate: tm?.referenceRate ?? null,
    spread: tm?.spread ?? null,
    cleared: false,
    clearedAt: null,
    clearedEligibleCount: null,
    clearedEligibleAmount: null,
    clearedEligibleDpeWeightedSum: null,
    clearedEligibleDpeWeightedAverage: null,
    clearedExcludedCount: null,
    clearedExcludedAmount: null,
  });

  return offerUuid;
}

/**
 * Updates an existing benchmark offer from PATCH /offers/benchmark.
 * Only frequency, expirationDate, and status can be changed — the rate is platform-determined.
 */
export async function editBenchmarkOffer(body: BenchmarkPatchBody): Promise<void> {
  const offerUuid = body.offers?.[0]?.uuid;
  if (!offerUuid) {
    throw new Error('editBenchmarkOffer: missing offers[0].uuid');
  }

  await offers.update((q) => q.where({ uuid: offerUuid }), {
    data(record) {
      if (body.status) {
        record.status = body.status as 'active' | 'paused' | 'inactive';
      }

      if (body.frequency) {
        record.frequency = normalizeFrequency(body.frequency);
      }

      if (body.expirationDate !== undefined) {
        record.expirationDate = body.expirationDate ?? null;
      }
    },
  });
}

/**
 * Creates or updates a static offer from POST /offers/static.
 *
 * Static offers carry a user-entered APR rate. The body shape is parallel to the
 * price-discovery POST — same supplierMarkets[0] identity fields, same rate/frequency/
 * expirationDate fields — so the underlying create/update logic is identical.
 *
 * Returns the uuid for the 201 response body (useSubmitStaticOffer reads body.data[0]?.uuid).
 */
export async function submitStaticOffer(body: StaticPostBody): Promise<string> {
  const sm = body.supplierMarkets?.[0];
  if (!sm) {
    throw new Error('submitStaticOffer: missing supplierMarkets[0]');
  }

  const key = marketKey(sm.marketUuid, sm.supplierDivisionUuid);

  const existingOffers = offers.findMany((q) => q.where({ marketKey: key }));
  const existingActive = existingOffers.find((o) => !o.cleared);

  if (existingActive) {
    await offers.update((q) => q.where({ uuid: existingActive.uuid }), {
      data(record) {
        record.rateType = 'APR';
        record.rate = body.rate ?? 0;
        record.frequency = normalizeFrequency(body.frequency);
        record.status = 'active';
        record.expirationDate = body.expirationDate ?? null;
      },
    });
    return existingActive.uuid;
  }

  const offerUuid = `DEMO-OFFER-${crypto.randomUUID()}`;
  const tm = takerMarkets.findFirst((q) => q.where({ key }));

  await offers.create({
    uuid: offerUuid,
    marketKey: key,
    marketUuid: sm.marketUuid,
    supplierDivisionUuid: sm.supplierDivisionUuid,
    makerOrganizationUuid: sm.makerOrganizationUuid ?? tm?.makerOrganizationUuid ?? '',
    rateType: 'APR',
    rate: body.rate ?? 0,
    frequency: normalizeFrequency(body.frequency),
    status: 'active',
    excludeNewInvoices: null,
    expirationDate: body.expirationDate ?? null,
    rateName: null,
    referenceRate: null,
    spread: null,
    cleared: false,
    clearedAt: null,
    clearedEligibleCount: null,
    clearedEligibleAmount: null,
    clearedEligibleDpeWeightedSum: null,
    clearedEligibleDpeWeightedAverage: null,
    clearedExcludedCount: null,
    clearedExcludedAmount: null,
  });

  return offerUuid;
}

/**
 * Creates or updates a demo express-accept offer from POST /offers/express-accept.
 *
 * Mirrors submitOffer — express-accept shares the same supplierMarkets[0] + rate/rateType/frequency
 * shape. Returns the uuid for the 201 response body (useSubmitExpressAcceptOffer reads data[0]?.uuid).
 */
export async function submitExpressAcceptOffer(body: ExpressAcceptPostBody): Promise<string> {
  const sm = body.supplierMarkets?.[0];
  if (!sm) {
    throw new Error('submitExpressAcceptOffer: missing supplierMarkets[0]');
  }

  const key = marketKey(sm.marketUuid, sm.supplierDivisionUuid);

  const existingOffers = offers.findMany((q) => q.where({ marketKey: key }));
  const existingActive = existingOffers.find((o) => !o.cleared);

  if (existingActive) {
    await offers.update((q) => q.where({ uuid: existingActive.uuid }), {
      data(record) {
        record.rateType = (body.rateType as 'APR' | 'DISC') ?? 'APR';
        record.rate = body.rate ?? 0;
        record.frequency = normalizeFrequency(body.frequency);
        record.status = 'active';
        record.expirationDate = null;
      },
    });
    return existingActive.uuid;
  }

  const offerUuid = `DEMO-EA-${crypto.randomUUID()}`;
  const tm = takerMarkets.findFirst((q) => q.where({ key }));

  await offers.create({
    uuid: offerUuid,
    marketKey: key,
    marketUuid: sm.marketUuid,
    supplierDivisionUuid: sm.supplierDivisionUuid,
    makerOrganizationUuid: sm.makerOrganizationUuid ?? tm?.makerOrganizationUuid ?? '',
    rateType: (body.rateType as 'APR' | 'DISC') ?? 'APR',
    rate: body.rate ?? 0,
    frequency: normalizeFrequency(body.frequency),
    status: 'active',
    excludeNewInvoices: null,
    expirationDate: null,
    rateName: null,
    referenceRate: null,
    spread: null,
    cleared: false,
    clearedAt: null,
    clearedEligibleCount: null,
    clearedEligibleAmount: null,
    clearedEligibleDpeWeightedSum: null,
    clearedEligibleDpeWeightedAverage: null,
    clearedExcludedCount: null,
    clearedExcludedAmount: null,
  });

  return offerUuid;
}

/**
 * Updates an existing static offer from PATCH /offers/static.
 *
 * Mirrors editOffer (price-discovery PATCH) — both use the same offers[0].uuid + rateInformation
 * body shape to update rate, frequency, status, and expirationDate.
 */
export async function editStaticOffer(body: StaticPatchBody): Promise<void> {
  const offerUuid = body.offers?.[0]?.uuid;
  if (!offerUuid) {
    throw new Error('editStaticOffer: missing offers[0].uuid');
  }

  await offers.update((q) => q.where({ uuid: offerUuid }), {
    data(record) {
      if (body.rateInformation?.rate !== undefined) {
        record.rate = body.rateInformation.rate;
      }

      if (body.frequency) {
        record.frequency = normalizeFrequency(body.frequency);
      }

      if (body.status) {
        record.status = body.status as 'active' | 'paused' | 'inactive';
      }

      if (body.expirationDate !== undefined) {
        record.expirationDate = body.expirationDate ?? null;
      }
    },
  });
}

/** Normalizes the API frequency enum ('ONE_TIME' | 'RECURRING' or lowercase variants) to the collection enum ('one-time' | 'recurring'). */
function normalizeFrequency(freq: string | undefined): 'one-time' | 'recurring' {
  return freq === 'one-time' ? 'one-time' : 'recurring';
}
